import React, {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { Icon, cx, useClickOutside } from './ui';

// Searchable single-select. Drop-in for native <select>: a trigger styled like a form
// input shows the selected label and opens a panel with a filter box on top and a
// scrollable list of options below. Clicking outside, Escape and Tab close it.
//
// Props:
//   value         — current value (string)
//   onChange      — (newValue) => void
//   options       — [{ value, label }] (the static list; also the initial list
//                   when onSearch is used)
//   onSearch      — OPTIONAL async (query) => [{ value, label }]. When provided the
//                   option list is server-driven (typeahead): the component calls
//                   onSearch(query) debounced as the user types instead of filtering
//                   `options` locally. Used for large sets (e.g. 20k users) so the
//                   full list is never shipped. When omitted, behaviour is unchanged
//                   (local filtering of `options`).
//   placeholder   — text shown when value is empty / no match (default "All")
//   allLabel      — label of the "clear filter" row at the top (default same
//                   as placeholder). Pass null to hide it.
//   searchPlaceholder — default "Filter…"
//   minWidth      — px (default 140)
//   maxHeight     — px height of the option list (default 260)
//   id            — OPTIONAL id for the trigger, so a <label htmlFor> can point at it
//   aria-labelledby — OPTIONAL id of the visible label. The trigger is then announced as
//                   the label followed by the selected option ("Tier, All tiers"). A
//                   <label htmlFor> alone cannot do that for a button: the label REPLACES
//                   the button's text as its name, so the current value would go unsaid.
//   aria-label    — OPTIONAL name when there is no visible label
//   aria-describedby — OPTIONAL, passed to the trigger
//   disabled      — OPTIONAL, renders the trigger disabled
//
// Keyboard: ArrowDown / ArrowUp on the trigger open it. In the filter box the arrows move
// the highlight while focus stays in the box (aria-activedescendant), Enter picks, Escape
// closes and returns focus to the trigger, Tab moves on and closes.
//
// The panel is position: fixed, placed from the trigger's rect, like components/ui/Menu:
// this select lives in card headers, filter rows and dialogs, and an absolutely
// positioned panel is cut off by the first ancestor that clips or scrolls. Unlike Menu it
// FOLLOWS the trigger on scroll and resize instead of closing: on a phone, focusing the
// filter box scrolls and resizes the page as the keyboard opens, and closing on that
// would shut the list the moment it opened.

const GAP = 4;          // between the trigger and the panel
const EDGE = 8;         // closest the panel comes to a viewport edge
const MIN_WIDTH = 200;  // .menu's own min-width
const MAX_WIDTH = 320;
// An async list opens almost empty ("Searching…") and fills a moment later, so the height
// it opens at says little about the room it will need. Roughly one page of results.
const ASYNC_ROOM = 300;

// Up or down is decided once, when the panel opens. Re-deciding on every scroll would flip
// it from one side of the trigger to the other under the pointer.
function opensUpward(rect, panel, remote) {
  const vh = window.innerHeight;
  const below = vh - rect.bottom - GAP - EDGE;
  const above = rect.top - GAP - EDGE;
  const needed = remote ? Math.max(panel.scrollHeight, ASYNC_ROOM) : panel.scrollHeight;
  return needed > below && above > below;
}

// Anchored by `bottom` when it opens upward, so a list that shrinks as the query narrows
// stays attached to the trigger instead of leaving a gap under itself.
function placement(rect, upward) {
  const vw = document.documentElement.clientWidth || window.innerWidth;
  const vh = window.innerHeight;
  const room = vw - EDGE * 2;
  const box = {
    minWidth: Math.min(Math.max(rect.width, MIN_WIDTH), room),
    maxWidth: Math.min(Math.max(rect.width, MAX_WIDTH), room),
  };
  // Left-aligned with the trigger while the widest the panel can grow still fits, else
  // right-aligned, so a select at the end of a row opens back over the row, not off it.
  if (rect.left + box.maxWidth <= vw - EDGE) box.left = Math.max(EDGE, rect.left);
  else if (rect.right - box.maxWidth >= EDGE) box.right = vw - rect.right;
  else box.left = EDGE;
  if (upward) {
    box.bottom = vh - rect.top + GAP;
    box.maxHeight = Math.max(0, rect.top - GAP - EDGE);
  } else {
    box.top = rect.bottom + GAP;
    box.maxHeight = Math.max(0, vh - rect.bottom - GAP - EDGE);
  }
  return box;
}

const BOX_KEYS = ['top', 'bottom', 'left', 'right', 'minWidth', 'maxWidth', 'maxHeight'];
const sameBox = (a, b) => BOX_KEYS.every((k) => a[k] === b[k]);

export default function SearchableSelect({
  value, onChange, options,
  onSearch,
  placeholder = 'All',
  allLabel,
  searchPlaceholder = 'Filter…',
  minWidth = 140,
  maxHeight = 260,
  id,
  disabled = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  'aria-describedby': ariaDescribedby,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dynamic, setDynamic] = useState([]);   // server-driven results (onSearch mode)
  const [loading, setLoading] = useState(false);
  const [pickedLabel, setPickedLabel] = useState(null); // remembers the chosen label
  const [active, setActive] = useState(-1);     // highlighted row, -1 = none
  const [box, setBox] = useState(null);         // the fixed panel's geometry, once measured
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const upwardRef = useRef(false);
  const focusedRef = useRef(false);   // the filter box has been focused for this opening
  const revealRef = useRef(false);    // scroll the highlight into view after this render
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const valueId = `${uid}-value`;
  const optionId = (i) => `${uid}-option-${i}`;
  const remote = !!onSearch;

  useClickOutside([wrapRef], () => setOpen(false), open);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  // Disabled while open (another filter just ruled this one out): close with it.
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);

  // Server-driven typeahead: (re)fetch on open + when the query changes, debounced.
  useEffect(() => {
    if (!onSearch || !open) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await onSearch(search.trim());
        if (!cancelled) setDynamic(Array.isArray(res) ? res : []);
      } catch {
        if (!cancelled) setDynamic([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [onSearch, open, search]);

  const localFiltered = useMemo(() => {
    const list = options || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, search]);

  const listOptions = onSearch ? dynamic : localFiltered;

  const selectedLabel = useMemo(() => {
    if (value === '' || value == null) return placeholder;
    const hit = (options || []).find(o => String(o.value) === String(value));
    if (hit) return hit.label;
    if (pickedLabel != null) return pickedLabel;  // onSearch mode: not in static list
    return placeholder;
  }, [options, value, placeholder, pickedLabel]);

  const clearLabel = allLabel === undefined ? placeholder : allLabel;
  const isEmpty = value === '' || value == null;

  // Everything the list shows, in order: the "All …" row, then the options. One array, so
  // the highlight is a single index whether it sits on the clear row or on an option.
  const rows = useMemo(() => {
    const out = clearLabel != null ? [{ clear: true, value: '', label: clearLabel }] : [];
    if (!loading) (listOptions || []).forEach(o => out.push({ clear: false, value: o.value, label: o.label }));
    return out;
  }, [clearLabel, loading, listOptions]);

  const isSelected = (row) => (row.clear ? isEmpty : String(row.value) === String(value));
  const activeIndex = active < rows.length ? active : rows.length - 1;

  const status = loading
    ? 'Searching…'
    : (listOptions || []).length === 0
      ? (onSearch && !search.trim() ? 'Type to search…' : 'No matches.')
      : null;

  // Where the highlight starts: on the current selection when the list opens (or the query
  // is emptied), and on the first real match while typing, so Enter takes what the query
  // found rather than the "All …" row pinned above it.
  useEffect(() => {
    if (!open) { setActive(-1); return; }
    let i = search.trim() ? rows.findIndex(r => !r.clear) : rows.findIndex(isSelected);
    if (i < 0) i = rows.length ? 0 : -1;
    revealRef.current = true;
    setActive(i);
  }, [open, search, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const place = useCallback((initial) => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const rect = trigger.getBoundingClientRect();
    if (initial) upwardRef.current = opensUpward(rect, panel, remote);
    const next = placement(rect, upwardRef.current);
    setBox(prev => (prev && sameBox(prev, next) ? prev : next));
  }, [remote]);

  // Measured after render, so the flip test knows the panel's real height. A layout
  // effect, so the panel is placed before it is ever painted.
  useLayoutEffect(() => {
    if (!open) {
      setBox(null);
      focusedRef.current = false;
      return;
    }
    place(true);
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onMove = (e) => {
      // The list's own scrolling moves nothing the panel is placed from.
      if (e.type === 'scroll' && e.target instanceof Node && panelRef.current?.contains(e.target)) return;
      place(false);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  // Focus moves to the filter box once the panel is in place. Not `autoFocus`: the first
  // render is an invisible measuring pass, and a hidden field cannot take focus.
  useEffect(() => {
    if (open && box && !focusedRef.current) {
      focusedRef.current = true;
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [open, box]);

  // Keyboard moves and the opening position scroll the highlight into view; hovering does
  // not, or pointing at a half-visible row would scroll the list out from under the mouse.
  useLayoutEffect(() => {
    if (!open || !box || !revealRef.current) return;
    revealRef.current = false;
    const list = listRef.current;
    const el = activeIndex >= 0 ? document.getElementById(optionId(activeIndex)) : null;
    if (!list || !el) return;
    if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop;
    else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight) {
      list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight;
    }
  });

  const close = (refocus) => {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  };

  const pick = (v, label) => { setPickedLabel(label ?? null); onChange(v); close(true); };

  const move = (step) => {
    if (!rows.length) return;
    revealRef.current = true;
    setActive((i) => {
      const from = i >= 0 && i < rows.length ? i : (step > 0 ? -1 : rows.length);
      return (from + step + rows.length) % rows.length;
    });
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Enter') {
      // Always swallowed: inside a <form>, Enter in this box would otherwise submit it.
      e.preventDefault();
      const row = rows[activeIndex];
      if (row) pick(row.value, row.clear ? null : row.label);
    } else if (e.key === 'Escape') {
      // Stopped here because Modal listens for Escape on window: one press closes the
      // list, not the dialog the list sits in.
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault();
      close(true);
    }
  };

  const onTriggerKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setOpen(true);
    }
  };

  // Focus moving to another control (Tab past the filter box) closes it. Only when focus
  // lands somewhere: a blur with no relatedTarget is a press on something unfocusable,
  // which on some phones includes the option being tapped, and closing then would remove
  // the option before its click arrived. Presses outside are the click-outside hook's job.
  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (!open || !next || wrapRef.current?.contains(next)) return;
    setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ minWidth }} onBlur={onBlur}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        className="form-input row gap-2"
        style={{ textAlign: 'left' }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-labelledby={ariaLabelledby ? `${ariaLabelledby} ${valueId}` : undefined}
        aria-label={!ariaLabelledby && ariaLabel ? `${ariaLabel}: ${selectedLabel}` : undefined}
        aria-describedby={ariaDescribedby}
        // While open, a press on the trigger must not pull focus out of the filter box:
        // the blur would close the list and the click would then open it again.
        onMouseDown={(e) => { if (open) e.preventDefault(); }}
        onClick={() => (open ? close(true) : setOpen(true))}
        onKeyDown={onTriggerKeyDown}
      >
        <span id={valueId} className={cx('grow truncate', isEmpty && 'text-muted')}>{selectedLabel}</span>
        <Icon name="chevronsUpDown" size={14} className="shrink-0 text-faint" />
      </button>
      {/* A press anywhere in the panel except the filter box keeps focus where it is.
          Without that, pressing an option blurred the box, and the blur closed the list
          before the click could land on the option. */}
      {open && (
        <div
          ref={panelRef}
          className="menu stack gap-0"
          style={{
            position: 'fixed',
            visibility: box ? 'visible' : 'hidden',
            ...(box || { top: 0, left: 0 }),
          }}
          onMouseDown={(e) => { if (e.target !== inputRef.current) e.preventDefault(); }}
        >
          <div className="search-input shrink-0 mb-1">
            <Icon name="search" className="search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="form-input"
              role="combobox"
              aria-expanded
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
              aria-label={String(searchPlaceholder).replace(/…$/, '')}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onSearchKeyDown}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-labelledby={ariaLabelledby || undefined}
            aria-label={ariaLabelledby ? undefined : (ariaLabel || placeholder)}
            className="m-0 p-0"
            style={{ listStyle: 'none', position: 'relative', overflowY: 'auto', maxHeight, minHeight: 0 }}
          >
            {rows.map((row, i) => {
              const selected = isSelected(row);
              return (
                <React.Fragment key={row.clear ? '__all__' : `v:${String(row.value)}`}>
                  <li
                    id={optionId(i)}
                    role="option"
                    aria-selected={selected}
                    data-active={i === activeIndex ? 'true' : undefined}
                    className={cx('menu-item', row.clear && 'text-muted', selected && 'fw-500')}
                    onMouseMove={() => { if (i !== activeIndex) setActive(i); }}
                    onClick={() => pick(row.value, row.clear ? null : row.label)}
                  >
                    <span className="grow break">{row.label}</span>
                    {selected && <Icon name="check" className="shrink-0 text-accent" />}
                  </li>
                  {row.clear && (rows.length > 1 || status) && (
                    <li role="presentation" className="menu-separator" />
                  )}
                </React.Fragment>
              );
            })}
          </ul>
          {status && <div className="px-3 py-2 text-sm text-muted" role="status">{status}</div>}
        </div>
      )}
    </div>
  );
}
